// Map search (spec 15c §3.6): an icon on phones, a field from `sm`; results as buttons.
import type { MapSystem } from '@stars-and-ships/api-types';
import { Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { type MapSearchResult, searchMap } from '../../map/search';
import { IconButton, inputFrame } from '../ui';

export function MapSearch({
  systems,
  onPick,
}: {
  systems: readonly MapSystem[];
  onPick(result: MapSearchResult): void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const results = useMemo(() => searchMap(systems, query), [systems, query]);
  const pick = (result: MapSearchResult) => {
    onPick(result);
    setQuery('');
    setOpen(false);
  };
  return (
    <div className="absolute top-2 right-2 z-40 flex flex-col items-end">
      <IconButton
        label={open ? 'Close search' : 'Open search'}
        className="hud-cut-sm bg-panel/80 sm:hidden"
        onClick={() => setOpen(!open)}
      >
        {open ? <X className="size-5" /> : <Search className="size-5" />}
      </IconButton>
      <div className={`${open ? 'block' : 'hidden'} mt-1 w-64 sm:mt-0 sm:block`}>
        <input
          aria-label="Search the map"
          placeholder="System or commander"
          className={`${inputFrame} bg-panel/90`}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {results.length > 0 ? (
          <ul className="mt-1 hud-cut-sm border border-line bg-panel/95 backdrop-blur">
            {results.map((r) => (
              <li key={r.kind === 'system' ? `s:${r.systemId}` : `c:${r.playerId}`}>
                <button
                  type="button"
                  data-map-search-result={
                    r.kind === 'system' ? `system:${r.systemId}` : `commander:${r.playerId}`
                  }
                  className="flex min-h-11 w-full flex-col items-start px-3 py-1.5 text-left hover:bg-panel-raised"
                  onClick={() => pick(r)}
                >
                  <span className="text-sm text-text">
                    {r.name}
                    {r.kind === 'commander' && r.tag ? ` [${r.tag}]` : ''}
                  </span>
                  <span className="text-xs text-muted">
                    {r.kind === 'system'
                      ? (r.ownerName ?? 'Unowned')
                      : `Commander · ${r.systemCount} system${r.systemCount === 1 ? '' : 's'}`}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
